import { SearchIcon } from "@chakra-ui/icons";
import { Badge, Box, HStack, InputGroup, InputRightElement, Text } from "@chakra-ui/react";
import { createColumnHelper } from "@tanstack/react-table";
import DataTable from "../../shared/components/DataTable/DataTable";
import useGraphQLDataTable from "../../shared/components/DataTable/useGraphQLDataTable";
import DebouncedInput from "../../shared/components/DebouncedInput";
import { EntityCardLink } from "../../shared/components/EntityCard";
import Page from "../../shared/components/Page";
import Select from "../../shared/components/Select";
import { PatientServicePlanStatus } from "../../shared/schema/gql/graphql";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { enumToOptions } from "../../shared/utils/enum-to-options";
import { getFullName } from "../../shared/utils/get-full-name";
import { EntitySelect } from "../workflow/components/EntityFormControl";
import ServicePlanSignatureClaimsCell, {
  SIGNATURE_ROLE_TITLES,
} from "./components/ServicePlanSignatureClaimsCell";
import ServicePlanStatusTag from "./components/ServicePlanStatusTag";
import ServicePlanTableActions from "./components/ServicePlanTableActions";
import { GET_SERVICE_PLANS_QUERY } from "./service-plan.graphql";
import { useServicePlansAiCapabilities } from "./servicePlans.ai";
import { ServicePlanRow } from "./types/service-plan-dashboard.types";

export default function ServicePlansDashboardPage() {
  const { dataTableProps, globalFilters, setFilter, query } = useGraphQLDataTable({
    document: GET_SERVICE_PLANS_QUERY,
    connection: "patientServicePlans",
    columns: columns,
    enableColumnFilters: false,
    columnVisiblity: {},
    initialSorting: [{ id: "createdAt", desc: true }],
    globalFilters: {
      // Agency-wide view of every plan by default — the status filter is opt-in.
      initialState: { search: "" },
    },
  });

  // This page tells the omnibar what its own filters are, while it is on screen.
  useServicePlansAiCapabilities({
    setFilter: (key, value) =>
      setFilter(key as Parameters<typeof setFilter>[0], value as Parameters<typeof setFilter>[1]),
  });

  const filtersNode = (
    <>
      <InputGroup width="md">
        <DebouncedInput
          debounce={200}
          placeholder="Search by patient's name or ID"
          value={globalFilters.search ?? ""}
          onChange={(x) => setFilter("search", x)}
        />
        <InputRightElement>
          <SearchIcon _groupFocusWithin={{ color: "blue" }} color="gray.400" />
        </InputRightElement>
      </InputGroup>
      <Select
        label="Statuses"
        multiple={true}
        options={enumToOptions(PatientServicePlanStatus)}
        value={globalFilters.statuses ?? null}
        onChange={(e) => setFilter("statuses", e)}
      />
      <EntitySelect
        input={{ type: "entity", entity: "Office" }}
        isDisabled={false}
        label="Office"
        multiple={true}
        renderUnselected="Office"
        value={globalFilters.officeIds ?? null}
        onChange={(officeIds) => setFilter("officeIds", officeIds)}
      />
    </>
  );

  return (
    <Box>
      <Page.Header>
        <Page.Title>
          Service Plans ({query.data?.patientServicePlans.totalCount ?? "..."})
        </Page.Title>
      </Page.Header>
      <DataTable {...dataTableProps} filterNode={filtersNode} />
    </Box>
  );
}

const { accessor, display } = createColumnHelper<ServicePlanRow>();

const columns = [
  accessor("patient", {
    header: "Patient",
    meta: { gqlSortKey: "patientId" },
    cell: ({ getValue }) => {
      const { id, displayId, gender, status } = getValue();
      return (
        <Box w="fit-content">
          <EntityCardLink
            boxProps={{ maxWidth: "fit-content" }}
            entity={{
              type: "Patient",
              fullName: getFullName(getValue()),
              status: status,
              id: id,
              displayId: displayId ?? null,
              gender: gender ?? null,
              contactDetails: null,
            }}
            stopPropagation={true}
          />
        </Box>
      );
    },
  }),
  accessor("patient.currentOffice.name", {
    header: "Office",
    cell: ({ getValue }) => getValue(),
  }),
  accessor("status", {
    header: "Status",
    meta: { gqlSortKey: "_status" },
    cell: ({ getValue }) => <ServicePlanStatusTag status={getValue()} />,
  }),
  accessor("startDate", {
    header: "Period",
    meta: { gqlSortKey: "startDate" },
    cell: ({ getValue, row }) => {
      const startDate = getValue();
      const endDate = row.original.endDate;
      return (
        <Text whiteSpace="nowrap">
          {startDate === null ? "—" : dateFormatter.toDate(startDate)}
          {" – "}
          {endDate === null ? "—" : dateFormatter.toDate(endDate)}
        </Text>
      );
    },
  }),
  accessor("program", {
    header: "Program",
    meta: { gqlSortKey: "program" },
    cell: ({ getValue }) => getValue() ?? "—",
  }),
  accessor("service", {
    header: "Service",
    meta: { gqlSortKey: "service" },
    cell: ({ getValue }) => getValue() ?? "—",
  }),
  accessor("caseManagerName", {
    header: "Case manager",
    meta: { gqlSortKey: "caseManagerName" },
    cell: ({ getValue }) => getValue() ?? "—",
  }),
  accessor("tasksCount", {
    header: "Tasks",
    meta: { gqlSortKey: "tasksCount" },
    cell: ({ getValue, row }) => (
      <HStack spacing={2}>
        <Text>{getValue()}</Text>
        {row.original.unmappedTasksCount > 0 && (
          <Badge colorScheme="orange">{row.original.unmappedTasksCount} unmapped</Badge>
        )}
      </HStack>
    ),
  }),
  display({
    id: "signatureLinks",
    header: "Signature links",
    meta: {
      csvFn: (_value, row) =>
        row.signatureClaims.nodes
          .map(
            (claim) =>
              `${SIGNATURE_ROLE_TITLES[claim.role]}: ${claim.recipientEmail} (${claim.status})`
          )
          .join("; "),
    },
    cell: ({ row }) => <ServicePlanSignatureClaimsCell plan={row.original} />,
  }),
  accessor("createdAt", {
    header: "Created",
    meta: { gqlSortKey: "createdAt" },
    cell: ({ getValue }) => dateFormatter.toDate(getValue()),
  }),
  display({
    id: "_actions",
    header: "Actions",
    cell: ({ row }) => <ServicePlanTableActions plan={row.original} />,
  }),
];
